import { Building2 } from 'lucide-react'
import { Dropdown } from '@/components/ui'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import { usePatrons } from '../hooks'

/**
 * Filter by patron.
 *
 * A Dropdown rather than a SegmentedControl, unlike Status and Owner: those
 * have three or four fixed options and a patron list grows without limit.
 *
 * "No patron" is a first-class option, not a footnote. It is the list of work
 * nobody has categorised yet, which is exactly what somebody tidying up needs —
 * and on a workspace that has just turned this on, it is everything.
 */
export function PatronFilter({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string) => void
}) {
  const t = useT()
  const { data: patrons, isPending } = usePatrons()
  const selected = patrons?.find((c) => c.id === value)
  const label =
    value === 'all'
      ? t('patronFilter.any')
      : value === 'none'
        ? t('patronFilter.none')
        : selected?.name ?? t('patronFilter.fallback')

  return (
    <Dropdown
      heading={t('patronFilter.heading')}
      trigger={({ isOpen, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={isOpen}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-sm',
            'text-fg-muted hover:bg-surface-raised hover:text-fg',
            value !== 'all' && 'border-brand/40 bg-brand/5 text-fg',
          )}
        >
          <Building2 aria-hidden className="h-3.5 w-3.5" />
          {label}
        </button>
      )}
      items={[
        { id: 'all', label: t('patronFilter.any'), selected: value === 'all', onSelect: () => onChange('all') },
        { id: 'none', label: t('patronFilter.none'), selected: value === 'none', onSelect: () => onChange('none') },
        ...(isPending
          ? [{ id: 'loading', label: t('common.loading'), disabled: true }]
          : (patrons ?? []).map((patron) => ({
              id: patron.id,
              label:
                patron.openActionCount > 0
                  ? `${patron.name} (${patron.openActionCount})`
                  : patron.name,
              selected: value === patron.id,
              onSelect: () => onChange(patron.id),
            }))),
        ...((patrons ?? []).length === 0 && !isPending
          ? [
              {
                id: 'empty',
                label: t('patronFilter.emptyHint'),
                disabled: true,
                separatorBefore: true,
              },
            ]
          : []),
      ]}
    />
  )
}
